import { Cartesian2, Cartesian3, IntersectionTests, Plane, Ray } from "cesium";
import { EarthBrainUtils } from "../../utilities/earthbrainutility";
import { SlopePointInfo } from "./slope-point-info";
export class SideSlopeGeometry {
    _root;
    _pointInfos;
    _verticalDirection;
    _intersectionStrategy;
    _generateShelf;
    _closeLoop;
    _settings;
    _geometry;
    get geometry() {
        return this._geometry;
    }
    get pointInfos() {
        return this._pointInfos;
    }
    constructor(args) {
        this._pointInfos = args.pointInfos;
        this._verticalDirection = args.verticalDirection;
        this._generateShelf = args.generateShelf;
        this._intersectionStrategy = args.intersectionStrategy;
        this._settings = args.settings;
        this._closeLoop = args.closeLoop;
        this._geometry = { vertices: [], indices: [] };
    }
    constructSlope(interpolationPitch, slopeConstructionStrategy) {
        const rowInfos = [];
        this._root = new SlopePointInfo(this._pointInfos[0].position, this._pointInfos[0].outwardDirection, 0);
        rowInfos.push(this._root);
        let slopePointInfo = this._root;
        for (let i = 1; i < this._pointInfos.length; ++i) {
            const node = new SlopePointInfo(this._pointInfos[i].position, this._pointInfos[i].outwardDirection, 0);
            node.back = slopePointInfo;
            slopePointInfo.front = node;
            slopePointInfo = node;
            rowInfos.push(node);
        }
        if (this._closeLoop) {
            const firstPointInfo = this._root;
            const lastPointInfo = rowInfos[rowInfos.length - 1];
            firstPointInfo.back = lastPointInfo;
            lastPointInfo.front = firstPointInfo;
        }
        //vertical slope points
        while (rowInfos.length > 0) {
            for (const node of rowInfos) {
                const { ray } = slopeConstructionStrategy.getSlopePoint(node, this._verticalDirection, this._settings);
                let hit;
                if (ray) {
                    hit = this._intersectionStrategy(ray);
                }
                if (hit) {
                    node.isAbleToHit = true;
                }
            }
            //calculate range such that start of range is ableToHit point and end point is ableToHit
            const nextRow = [];
            const ranges = [];
            const processedNodes = new Set();
            for (const node of rowInfos) {
                if (processedNodes.has(node)) {
                    continue;
                }
                processedNodes.add(node);
                if (node.isLeaf) {
                    continue;
                }
                if (!node.isAbleToHit) {
                    continue;
                }
                if (node.isAbleToHit) {
                    let startNode;
                    let endNode;
                    // endNode
                    let current = node;
                    while (current) {
                        processedNodes.add(current);
                        if (!current.front) {
                            //undefined
                            endNode = current;
                            break;
                        }
                        if (current.front === node) {
                            //complete loop
                            startNode = node;
                            endNode = current;
                            break;
                        }
                        if (current.front.isLeaf) {
                            //leaf
                            endNode = current;
                            break;
                        }
                        current = current.front;
                    }
                    if (!startNode) {
                        current = node;
                        while (current) {
                            processedNodes.add(current);
                            if (!current.back) {
                                //undefined
                                startNode = current;
                                break;
                            }
                            if (current.back === node) {
                                //complete loop
                                startNode = current;
                                endNode = node;
                                break;
                            }
                            if (current.back.isLeaf) {
                                //leaf
                                startNode = current;
                                break;
                            }
                            current = current.back;
                        }
                    }
                    if (startNode && endNode) {
                        startNode !== endNode &&
                            ranges.push({ start: startNode, end: endNode });
                    }
                    else if (endNode) {
                        node !== endNode && ranges.push({ start: node, end: endNode });
                    }
                    else if (startNode) {
                        startNode !== node && ranges.push({ start: startNode, end: node });
                    }
                }
            }
            //checkif node is able to hit
            for (const range of ranges) {
                let node = range.start;
                while (node) {
                    const { ray, point: probablePoint } = node.treeHeight % 2 !== 0 && this._generateShelf
                        ? // grasp error, are there any reason?
                            slopeConstructionStrategy.getShelfPoint(node, this._verticalDirection, this._settings)
                        : slopeConstructionStrategy.getSlopePoint(node, this._verticalDirection, this._settings);
                    // D3D-353: when error occurs, probablePoint is undefined
                    // and if the probablePoint is undefined, the constructor of SlopePointInfo make error
                    // Note: probablePoint shouldn't be undefined in mathematical. It code is just in case to solve this problem
                    if (!probablePoint) {
                        if (node === range.end) {
                            break;
                        }
                        console.warn("probablePoint is undefined");
                        node = node.front;
                        continue;
                    }
                    const downNode = new SlopePointInfo(probablePoint, node.outwardDirection.clone(), node.treeHeight + 1);
                    node.down.push(downNode);
                    downNode.up.push(node);
                    downNode.crossSectionalplane = node.crossSectionalplane;
                    downNode.outwardDirection = node.outwardDirection;
                    let hit;
                    if (ray) {
                        hit = this._intersectionStrategy(ray);
                    }
                    if (hit) {
                        const distanceToProbablePoint = Cartesian3.distance(node.position, probablePoint);
                        const distanceToHit = Cartesian3.distance(node.position, hit);
                        if (distanceToHit - distanceToProbablePoint <= 0.0000001) {
                            //hit heightmap
                            downNode.probablePosition = probablePoint;
                            downNode.position = hit;
                            downNode.isLeaf = true;
                        }
                    }
                    if (node === range.end) {
                        break;
                    }
                    node = node.front;
                }
            }
            for (const range of ranges) {
                let node = range.start;
                // At this point all parents points have exactly one down node except the leaf node
                // using the parents plane, check if the down points crosses the plane in back or front side
                // If it crosses, assign the point of the correct one
                // if crosses, it should take the point of the plane
                const convergingNodeList = [];
                while (node) {
                    // group probable parents and work through the one with heighest number of parents
                    //front
                    if (!node.back || !node.front) {
                        node = node.front;
                        continue;
                    }
                    let frontNode = node;
                    const probableFrontNodes = [];
                    while (frontNode.down[0] && frontNode.front?.down[0]) {
                        const slopeDir = EarthBrainUtils.getDirBetweenTwoCartesians(frontNode.front.position, frontNode.front.down[0].position);
                        const ray = new Ray(frontNode.front.position, slopeDir);
                        const hit = IntersectionTests.rayPlane(ray, node.crossSectionalplane);
                        if (hit) {
                            const distanceWithHit = Cartesian3.distance(frontNode.front.position, hit);
                            const slopeDistance = Cartesian3.distance(frontNode.front.position, frontNode.front.down[0].position);
                            if (distanceWithHit < slopeDistance) {
                                probableFrontNodes.push({ node: frontNode.front, hit });
                            }
                            else {
                                break;
                            }
                        }
                        else {
                            break;
                        }
                        frontNode = frontNode.front;
                    }
                    let backNode = node;
                    const probableBackNodes = [];
                    while (backNode.down[0] && backNode.back?.down[0]) {
                        const slopeDir = EarthBrainUtils.getDirBetweenTwoCartesians(backNode.back.position, backNode.back.down[0].position);
                        const ray = new Ray(backNode.back.position, slopeDir);
                        const hit = IntersectionTests.rayPlane(ray, node.crossSectionalplane);
                        if (hit) {
                            const distanceWithHit = Cartesian3.distance(backNode.back.position, hit);
                            const slopeDistance = Cartesian3.distance(backNode.back.position, backNode.back.down[0].position);
                            if (distanceWithHit < slopeDistance) {
                                probableBackNodes.push({ node: backNode.back, hit });
                            }
                            else {
                                break;
                            }
                        }
                        else {
                            break;
                        }
                        backNode = backNode.back;
                    }
                    if (probableFrontNodes.length > 0 || probableBackNodes.length > 0) {
                        const outgoingVec2 = Cartesian2.subtract(node.back.position, node.position, new Cartesian2());
                        const outgoingVec1 = Cartesian2.subtract(node.front.position, node.position, new Cartesian2());
                        const outgoingAngle2 = Cartesian2.angleBetween(outgoingVec2, node.outwardDirection);
                        const outgoingAngle1 = Cartesian2.angleBetween(outgoingVec1, node.outwardDirection);
                        const outWardAngle = outgoingAngle2 + outgoingAngle1;
                        convergingNodeList.push({
                            outWardAngle,
                            node,
                            front: [...probableFrontNodes],
                            back: [...probableBackNodes],
                        });
                    }
                    if (node === range.end) {
                        break;
                    }
                    node = node.front;
                }
                convergingNodeList.sort((a, b) => {
                    return a.outWardAngle - b.outWardAngle;
                });
                const visitedNodes = [];
                for (const nodeObj of convergingNodeList) {
                    if (visitedNodes.includes(nodeObj.node)) {
                        continue;
                    }
                    const convergingNodes = [...nodeObj.back.reverse(), ...nodeObj.front];
                    // const hit2DistPos = [...convergingNodes, { hit: nodeObj.node.down[0].position }].map((obj) =>
                    //     Cartesian2.distance(nodeObj.node.position, obj.hit),
                    // );
                    // const maxDist = Math.max(...hit2DistPos);
                    // const probableDownPosition = Cartesian3.add(
                    //     nodeObj.node.position,
                    //     Cartesian3.multiplyByScalar(nodeObj.node.outwardDirection, maxDist, new Cartesian3()),
                    //     new Cartesian3(),
                    // );
                    // probableDownPosition.z = nodeObj.node.down[0].position.z;
                    // Cartesian3.clone(probableDownPosition, nodeObj.node.down[0].position);
                    nodeObj.node.down[0].crossSectionalplane =
                        nodeObj.node.crossSectionalplane;
                    nodeObj.node.down[0].outwardDirection = nodeObj.node.outwardDirection;
                    nodeObj.node.down[0].isMerged = true;
                    nodeObj.node.down[0].up = [
                        ...nodeObj.back.reverse().map((n) => n.node),
                        nodeObj.node,
                        ...nodeObj.front.map((n) => n.node),
                    ];
                    visitedNodes.push(nodeObj.node);
                    for (const pnode of convergingNodes) {
                        pnode.node.down = [nodeObj.node.down[0]];
                        visitedNodes.push(pnode.node);
                    }
                }
                node = range.start;
                while (node) {
                    if (node.front &&
                        node.down.length > 0 &&
                        node.front.down.length > 0) {
                        if (node.down[node.down.length - 1] === node.front.down[0]) {
                            if (node === range.end) {
                                if (node.front &&
                                    node.down.length > 0 &&
                                    node.front.down.length > 0 &&
                                    node.down !== node.front.down &&
                                    node.front.down[0] !== node.down[node.down.length - 1]) {
                                    node.down[node.down.length - 1].front = node.front.down[0];
                                    node.front.down[0].back = node.down[node.down.length - 1];
                                }
                                break;
                            }
                            node = node.front;
                            continue;
                        }
                        if (!node.down[node.down.length - 1].isLeaf &&
                            !node.front.down[0].isLeaf) {
                            const dist = Cartesian3.distance(node.down[node.down.length - 1].probablePosition, node.front.down[0].probablePosition);
                            if (dist > 1.5 * interpolationPitch) {
                                //add new Point in the middle
                                const newPoint = Cartesian3.add(node.down[node.down.length - 1].probablePosition, node.front.down[0].probablePosition, new Cartesian3());
                                Cartesian3.multiplyByScalar(newPoint, 0.5, newPoint);
                                const outwardDirection = Cartesian3.add(node.down[0].outwardDirection, node.front.down[0].outwardDirection, new Cartesian3());
                                Cartesian3.normalize(outwardDirection, outwardDirection);
                                const newNode = new SlopePointInfo(newPoint, outwardDirection, node.down[0].treeHeight);
                                //relation
                                newNode.up.push(node, node.front);
                                newNode.back = node.down[node.down.length - 1];
                                newNode.front = node.front.down[0];
                                newNode.computeOutwardDirection();
                                newNode.isAbleToHit =
                                    newNode.front.isAbleToHit || newNode.back.isAbleToHit;
                                node.down[node.down.length - 1].front = newNode;
                                node.front.down[0].back = newNode;
                                node.down.push(newNode);
                                node.front.down.unshift(newNode);
                            }
                            else {
                                if (node.down !== node.front.down) {
                                    node.down[node.down.length - 1].front = node.front.down[0];
                                    node.front.down[0].back = node.down[node.down.length - 1];
                                }
                            }
                        }
                        else {
                            if (node.down !== node.front.down) {
                                node.down[node.down.length - 1].front = node.front.down[0];
                                node.front.down[0].back = node.down[node.down.length - 1];
                            }
                        }
                    }
                    if (node === range.end) {
                        break;
                    }
                    node = node.front;
                }
                node = range.start;
                while (node) {
                    if (node.isAbleToHit) {
                        for (const downInfo of node.down) {
                            if (!nextRow.includes(downInfo)) {
                                nextRow.push(downInfo);
                                // !downInfo.isLeaf && nextRow.push(downInfo);
                            }
                        }
                    }
                    if (node === range.end) {
                        break;
                    }
                    node = node.front;
                }
            }
            rowInfos.length = 0;
            for (const nextRowNode of nextRow) {
                if (!nextRowNode.isLeaf && (nextRowNode.back || nextRowNode.front)) {
                    rowInfos.push(nextRowNode);
                }
            }
        }
    }
    addToGeometry(fp, sp, tp) {
        this._geometry.vertices.push(fp.x, fp.y, fp.z);
        this._geometry.indices.push(this._geometry.indices.length);
        this._geometry.vertices.push(sp.x, sp.y, sp.z);
        this._geometry.indices.push(this._geometry.indices.length);
        this._geometry.vertices.push(tp.x, tp.y, tp.z);
        this._geometry.indices.push(this._geometry.indices.length);
    }
    buildGeometry() {
        this._geometry = { vertices: [], indices: [] };
        const visitedNodes = [];
        const leafNodes = [];
        //bfs
        const addNodeToFIFO = (fifo, node) => {
            if (!visitedNodes.includes(node)) {
                if (fifo[fifo.length - 1] !== node) {
                    fifo.push(node);
                    visitedNodes.push(node);
                    if (node.isLeaf) {
                        leafNodes.push(node);
                    }
                    else if (!node.isAbleToHit && node.down.length === 0) {
                        leafNodes.push(node);
                    }
                }
            }
        };
        const fifo = [];
        addNodeToFIFO(fifo, this._root);
        while (fifo.length > 0) {
            const currentNode = fifo.shift();
            if (currentNode.back &&
                currentNode.back.down.length > 0 &&
                currentNode.down.length > 0) {
                const fp = currentNode.position;
                const sp = currentNode.down[0].position;
                const tp = currentNode.back.down[currentNode.back.down.length - 1].position;
                if (!Cartesian3.equals(sp, tp)) {
                    this.addToGeometry(fp, sp, tp);
                }
            }
            for (let i = 0; i < currentNode.down.length - 1; ++i) {
                const fp = currentNode.position;
                const sp = currentNode.down[i + 1].position;
                const tp = currentNode.down[i].position;
                this.addToGeometry(fp, sp, tp);
            }
            if (currentNode.front && currentNode.down.length > 0) {
                const fp = currentNode.position;
                const sp = currentNode.front.position;
                const tp = currentNode.down[currentNode.down.length - 1].position;
                this.addToGeometry(fp, sp, tp);
            }
            currentNode.down.forEach((node) => addNodeToFIFO(fifo, node));
            if (currentNode.front && !currentNode.isSibling(currentNode.front)) {
                addNodeToFIFO(fifo, currentNode.front);
            }
        }
        //post processing
        // front
        for (const node of leafNodes) {
            if (node.front?.isLeaf) {
                continue;
            }
            if (node.front) {
                this.addVerticesForFrontSlopePointInfo(node.position, node.front);
            }
        }
        //back
        for (const node of leafNodes) {
            if (node.back?.isLeaf) {
                continue;
            }
            if (node.back) {
                this.addVerticesForBackSlopePointInfo(node.position, node.back);
            }
        }
    }
    addConnectingPoint(point, isAtBeginning) {
        if (isAtBeginning) {
            this.addVerticesForFrontSlopePointInfo(point, this._root);
        }
        else {
            let node = this._root.back;
            if (!node) {
                node = this._root;
                while (node.front) {
                    node = node.front;
                }
            }
            this.addVerticesForBackSlopePointInfo(point, node, true);
        }
    }
    addVerticesForFrontSlopePointInfo(refPos, node) {
        const generations = [];
        let child = node;
        while (child) {
            generations.push(child);
            child = child.down[0];
        }
        const lastGeneration = generations.pop();
        //create a intersecting plane between node and last generation
        const dirToLastGen = EarthBrainUtils.getDirBetweenTwoCartesians(refPos, lastGeneration.position);
        const normal = Cartesian3.cross(dirToLastGen, Cartesian3.UNIT_Z, new Cartesian3());
        Cartesian3.normalize(normal, normal);
        const plane = Plane.fromPointNormal(refPos, normal);
        const dirToPoint = EarthBrainUtils.getDirBetweenTwoCartesians(node.position, refPos);
        const ray = new Ray(node.position, dirToPoint);
        let previousHit = refPos;
        let previousGen = node.position;
        for (const gen of generations) {
            Cartesian3.clone(gen.position, ray.origin);
            const hit = IntersectionTests.rayPlane(ray, plane);
            if (hit) {
                const fp = previousHit;
                const sp = previousGen;
                const tp = hit;
                this.addToGeometry(fp, sp, tp);
                const fp1 = previousGen;
                const sp1 = gen.position;
                const tp1 = hit;
                this.addToGeometry(fp1, sp1, tp1);
            }
            else {
                break;
            }
            previousGen = gen.position;
            previousHit = hit;
        }
        if (previousHit && previousGen) {
            const fp = previousHit;
            const sp = previousGen;
            const tp = lastGeneration.position;
            this.addToGeometry(fp, sp, tp);
        }
    }
    addVerticesForBackSlopePointInfo(refPos, node, isConnectingPoint = false) {
        const generations = [];
        let child = node;
        while (child) {
            generations.push(child);
            child = child.down[child.down.length - 1];
        }
        const lastGeneration = generations.pop();
        //create a intersecting plane between node and last generation
        const dirToLastGen = EarthBrainUtils.getDirBetweenTwoCartesians(refPos, lastGeneration.position);
        const normal = Cartesian3.cross(dirToLastGen, Cartesian3.UNIT_Z, new Cartesian3());
        Cartesian3.normalize(normal, normal);
        const plane = Plane.fromPointNormal(refPos, normal);
        const dirToPoint = EarthBrainUtils.getDirBetweenTwoCartesians(node.position, refPos);
        const ray = new Ray(node.position, dirToPoint);
        let previousHit = refPos;
        let previousGen = node.position;
        let keepFirstTriangle = isConnectingPoint;
        generations.shift();
        for (const gen of generations) {
            Cartesian3.clone(gen.position, ray.origin);
            const hit = IntersectionTests.rayPlane(ray, plane);
            if (hit) {
                if (keepFirstTriangle) {
                    const fp = previousGen;
                    const sp = previousHit;
                    const tp = gen.position;
                    this.addToGeometry(fp, sp, tp);
                }
                keepFirstTriangle = true;
                const fp1 = previousHit;
                const sp1 = hit;
                const tp1 = gen.position;
                this.addToGeometry(fp1, sp1, tp1);
            }
            else {
                break;
            }
            previousGen = gen.position;
            previousHit = hit;
        }
        if ((generations.length > 0 || isConnectingPoint) &&
            previousHit &&
            previousGen) {
            const fp = previousGen;
            const sp = previousHit;
            const tp = lastGeneration.position;
            this.addToGeometry(fp, sp, tp);
        }
    }
}
//# sourceMappingURL=side-slope-geometry.js.map